import { Vector3, AbstractMesh, AnimationGroup, Color4, Color3, PointLight } from '@babylonjs/core';
import { StatePhase } from '../config/characters';
import type { Character } from '../systems/Character';

// ============================================================================
// CHANNEL ACTIONS — Capabilities provided to hooks
// ============================================================================

/**
 * Particle spawn options for channel actions
 */
export interface ChannelParticleOptions {
  /** Preset name for particle effect */
  preset?: string;
  /** Override position (default: bone position) */
  position?: Vector3;
  /** Override direction (default: character facing) */
  direction?: Vector3;
  /** Scale multiplier */
  scale?: number;
  /** Color override */
  color?: Color4;
  /** Spawn count multiplier */
  countMultiplier?: number;
}

/**
 * Hit detection configuration for channel attacks
 */
export interface ChannelHitDetectionConfig {
  type: 'cone' | 'cylinder' | 'sphere';
  range: number;
  /** Cone half-angle in degrees, or cylinder/sphere radius */
  width: number;
  /** Damage per hit */
  damage?: number;
  /** Minimum time between damage ticks to same target (seconds) */
  damageInterval?: number;
}

/**
 * Options for spawning particles at a bone with enhanced control
 */
export interface BoneParticleOptions {
  /** Particle preset name */
  preset: string;
  /** Bone to spawn from */
  bone: string;
  /** Local offset from bone position */
  offset?: Vector3;
  /** Direction mode or explicit direction */
  direction?: 'facing' | 'bone' | Vector3;
  /** Randomization options */
  jitter?: {
    /** Random position offset (world units) */
    position?: number;
    /** Random direction offset (degrees) */
    direction?: number;
    /** Random scale variation (e.g., 0.2 = ±20%) */
    scale?: number;
  };
  /** Scale multiplier */
  scale?: number;
  /** Color override */
  color?: Color4;
  /** Number of particles to spawn */
  count?: number;
}

/**
 * Actions available during channeled attack hooks.
 * Similar to CollisionActions pattern from EntityTypes.
 */
export interface ChannelActions {
  // === Particles ===
  
  /** Start continuous particle emission (returns emission ID for stopping) */
  startParticles(options: ChannelParticleOptions): string;
  
  /** Stop continuous particle emission by ID */
  stopParticles(emissionId: string): void;
  
  /** Spawn one-shot particle burst */
  burstParticles(preset: string, options?: Partial<ChannelParticleOptions>): void;
  
  /** Update direction of active particle emission */
  updateParticleDirection(emissionId: string, direction: Vector3): void;
  
  /** 
   * Spawn particles at a bone position with offset and jitter.
   * More ergonomic than getBonePosition + burstParticles.
   */
  spawnParticlesAtBone(options: BoneParticleOptions): void;
  
  // === Hit Detection ===
  
  /** Perform hit detection and return targets in range */
  detectTargets(config: ChannelHitDetectionConfig): ChannelHitResult[];
  
  /** Apply damage to a specific target */
  applyDamage(targetId: string, amount: number): void;
  
  /** Apply damage to all targets from last detectTargets call */
  applyDamageToAll(amount: number): void;
  
  // === Character State ===
  
  /** Get character's current facing direction */
  getFacing(): Vector3;
  
  /** Get character's current position */
  getPosition(): Vector3;
  
  /** Get bone world position */
  getBonePosition(boneName: string): Vector3 | null;
  
  /** Get bone world forward direction */
  getBoneForward(boneName: string): Vector3 | null;
  
  // === Effects ===
  
  /** Trigger screen impact effect */
  triggerImpact(preset: 'small' | 'medium' | 'large', intensity?: number): void;
  
  /** Play sound effect */
  playSound(soundId: string): void;
  
  // === Lights ===
  
  /** Create a temporary light attached to a bone (returns light ID for cleanup) */
  createBoneLight(options: BoneLightOptions): string;
  
  /** Update position of an existing bone light */
  updateBoneLightPosition(lightId: string, boneName: string, offset?: Vector3): void;
  
  /** Update color/intensity of an existing bone light */
  updateBoneLightColor(lightId: string, color: Color3, intensity?: number): void;
  
  /** Destroy a temporary light by ID */
  destroyLight(lightId: string): void;
}

/**
 * Options for creating a light attached to a bone
 */
export interface BoneLightOptions {
  /** Bone to attach to */
  bone: string;
  /** Light color (default white) */
  color?: Color3;
  /** Light intensity (default 1.0) */
  intensity?: number;
  /** Light range in units (default 5.0) */
  range?: number;
  /** Offset from bone position (local space) */
  offset?: Vector3;
}

/**
 * Result from channel hit detection
 */
export interface ChannelHitResult {
  targetId: string;
  target: Character;
  distance: number;
  position: Vector3;
  /** Time since last damage to this target (undefined if never hit) */
  timeSinceDamage?: number;
  /** Whether damage can be applied (respects damageInterval) */
  canDamage: boolean;
}

// ============================================================================
// CHANNEL PRESETS — Stackable, reusable behavior configurations
// ============================================================================

/**
 * A channel preset defines behavior that runs during a channeled attack.
 * Presets are stackable - multiple presets execute in order.
 */
export interface ChannelPreset {
  /** Unique identifier for this preset */
  id: string;
  
  /** Human-readable name */
  name?: string;
  
  /** 
   * Optional static FX configurations for this preset.
   * Exposed so CombatSystem can spawn windup-phase FX before channel starts.
   */
  staticFxConfig?: {
    id?: string;
    assetPath: string;
    boneName?: string;
    scale?: number;
    offset?: { x: number; y: number; z: number };
    orientationMode?: 'bone' | 'world' | 'facing';
    forward?: { x: number; y: number; z: number };
    up?: { x: number; y: number; z: number };
    localRotation?: { x?: number; y?: number; z?: number };
    startTime?: number;
    endTime?: number;
    playbackRate?: number;
    videoLoopRange?: { start: number; end: number };
    spawnPhase?: 'channel' | 'windup';
  }[];
  
  /** Called when channel starts */
  onStart?: (ctx: ChannelPresetContext) => void;
  
  /** Called each tick during channel */
  onTick?: (ctx: ChannelPresetContext) => void;
  
  /** Called when channel ends */
  onEnd?: (ctx: ChannelPresetContext & { reason: 'completed' | 'interrupted' | 'cancelled' }) => void;
}

/**
 * Context provided to channel presets
 */
export interface ChannelPresetContext {
  /** Attack definition */
  readonly attack: AttackDef;
  
  /** Character performing the attack */
  readonly attackerId: string;
  
  /** Duration channel has been active (seconds) */
  readonly channelDuration: number;
  
  /** Number of ticks elapsed */
  readonly tickCount: number;
  
  /** Whether the attack button is still held */
  readonly buttonHeld: boolean;
  
  /** Max duration if configured */
  readonly maxDuration?: number;
  
  /** Normalized channel progress (0-1) if maxDuration is set */
  readonly normalizedTime: number;
  
  /** Actions API for this channel */
  readonly actions: ChannelActions;
  
  /** Preset-specific configuration passed at creation */
  readonly config: Record<string, unknown>;
  
  /** Shared state across presets (for communication) */
  state: Record<string, unknown>;
}

/**
 * Factory function type for creating configured presets
 */
export type ChannelPresetFactory<TConfig = Record<string, unknown>> = 
  (config: TConfig) => ChannelPreset;

// ============================================================================
// DAMAGE CURVES — Reusable damage modifiers over time
// ============================================================================

/**
 * Built-in damage curve types
 */
export type DamageCurveType = 'constant' | 'rampUp' | 'rampDown' | 'pulse' | 'burst';

/**
 * Damage curve function: takes normalized time (0-1), returns multiplier
 */
export type DamageCurveFunction = (t: number) => number;

/**
 * Damage curve configuration
 */
export interface DamageCurveConfig {
  type: DamageCurveType | DamageCurveFunction;
  /** Base damage per tick */
  baseDamage: number;
  /** Curve-specific parameters */
  params?: {
    /** For rampUp: final multiplier (default 2.0) */
    maxMultiplier?: number;
    /** For pulse: frequency in Hz (default 2.0) */
    pulseFrequency?: number;
    /** For burst: damage on first tick multiplier (default 3.0) */
    burstMultiplier?: number;
  };
}

// ============================================================================
// WEAPON CONFIGURATION
// ============================================================================

/**
 * Weapon-specific configuration.
 */
export interface WeaponConfig {
  meleeRange?: number; // Melee attack range in meters (default 2.0)
}

/**
 * Attack definition from character config.
 * Defines the attack animation, damage, and timeline events.
 */
export type AttackType = 'oneshot' | 'channeled';

export interface InterruptionPolicy {
  allowDamageInterrupt?: boolean; // Default true
  allowPlayerCancel?: boolean; // Default false
  allowedPhases?: StatePhase[]; // If set, only these phases are interruptible
  customCheck?: (context: InterruptContext) => boolean | void; // Return true to allow, false to block
}

export interface AttachedMeshSpec {
  assetId: string;
  assetPath: string;
  attachBone: string;
  scale?: number;
  localTransform?: {
    position?: Vector3;
    rotation?: Vector3;
    scale?: Vector3;
  };
  animationName?: string; // Optional animation to play on the attached mesh
}

export interface AttachedMeshInstance {
  mesh: AbstractMesh;
  boneName: string;
  spec: AttachedMeshSpec;
  animationGroup?: AnimationGroup;
}

export interface ChanneledAttackConfig {
  loopAnimation: string;
  endAnimation?: string;
  tickInterval?: number; // Seconds between ticks (default 0.1)
  lockFacing?: boolean; // Default false (allow facing updates)
  lockMovement?: boolean; // Default false (prevent movement during channel)
  minDuration?: number; // Minimum seconds before early exit is allowed
  attachedMeshes?: AttachedMeshSpec[];
  maxDuration?: number; // Optional max duration in seconds
  
  /** Static visual FX that spawn at channel start and stay attached to bones (no collision/movement) */
  staticFx?: ProjectileSpec[];
  
  /** Stackable presets that execute in order during channel */
  presets?: ChannelPreset[];
  
  /** Preset configurations keyed by preset ID (passed to preset context) */
  presetConfigs?: Record<string, Record<string, unknown>>;
}

export interface AttackDef {
  id: string;
  animation: string;
  damage: number;
  maskBones?: string[]; // Action-layer mask (e.g., upper body for attack overlay)
  applyMaskWhenInStates?: string[]; // Only apply mask when base animation is in one of these states (e.g., ['run', 'walk'])
  disableRootMotionWhenMasked?: boolean; // If true, root motion is disabled when mask is active (prevents drift during movement attacks)
  speedWhenMasked?: number; // Animation speed when mask is active (default: use animation's defaultSpeed)
  speedWhenUnmasked?: number; // Animation speed when mask is NOT active (default: use animation's defaultSpeed)
  blendIn?: number; // Fade-in duration in seconds (default 0.1)
  blendOut?: number; // Fade-out duration in seconds (default 0.15)
  events: AttackEvent[];
  projectile?: ProjectileSpec;
  onProjectileHandover?: (context: ProjectileHandoverContext) => ProjectileLaunchDirective | void;
  type?: AttackType; // Default 'oneshot'
  cooldown?: number; // Seconds before attack can be used again
  globalCooldown?: number; // Applies to all attacks for this character
  interruptionPolicy?: InterruptionPolicy;
  channeled?: ChanneledAttackConfig;
  
  // Impact FX configuration
  impactFX?: import('../systems/effects/FXPresets').FXConfig;
  
  // Hook surface
  onInterruptCheck?: (context: InterruptContext) => boolean | void;
  onAttackTick?: (context: AttackTickContext) => void;
  onChannelStart?: (context: ChannelStartContext) => void;
  onChannelTick?: (context: ChannelTickContext) => void;
  onChannelEnd?: (context: ChannelEndContext) => void;
  onAttackEnd?: (context: AttackEndContext) => void;
  onAttackCancel?: (context: AttackCancelContext) => void;
  onPhaseChange?: (from: StatePhase | null, to: StatePhase) => void;
  
  // Weapon asset configuration
  weaponAssetPath?: string; // Path to weapon glTF asset
  weaponScale?: number; // Scale multiplier for weapon (default 1.0)
  weaponAttachBone?: string; // Bone to attach weapon to (default 'Hand_R')
  weaponConfig?: WeaponConfig; // Weapon-specific settings
  keepWeaponAttached?: boolean; // If true, weapon remains visible after attack (default true for melee)
}

/**
 * Timeline event during an attack animation (normalized time 0-1).
 */
export interface AttackEvent {
  time: number; // 0-1, normalized animation time
  type: 'attachWeapon' | 'detachProjectile' | 'enableHitbox' | 'disableHitbox' | 'playWeaponAnim';
  params?: any; // Event-specific data
}

/**
 * Projectile specification (kinematic, ballistic, or static FX).
 */
export interface ProjectileSpec {
  assetId: string;
  assetPath?: string; // Path to projectile glTF asset
  scale?: number; // Scale multiplier for projectile (default 1.0)
  type: 'kinematic' | 'ballistic' | 'static'; // kinematic = raycast, ballistic = physics, static = FX only (no movement/collision)
  speed: number; // units/sec (kinematic) or initial velocity (ballistic), ignored for static
  directionMode: 'facing' | 'bone' | 'aim'; // How to determine projectile direction
  muzzleBone?: string; // Bone to spawn projectile from (if directionMode = 'bone')
  hitRadius?: number; // Collision radius for hit detection (default 0 = thin raycast, >0 = sphere sweep)
  
  // Physics (ballistic only)
  gravityScale?: number; // Multiplier on world gravity (default 1.0)
  bounciness?: number; // 0 = no bounce, 1 = perfect bounce (default 0.5)
  friction?: number; // Surface friction (default 0.5)
  mass?: number; // Physics mass in kg (default 1.0)
  maxLifetime?: number; // Seconds until auto-destroy (default 10)
  maxDistance?: number; // Max travel distance in meters (default 20)
  
  // Sticky grenade behavior
  stickyOnImpact?: boolean; // Attach to surface on first contact (default false)
  
  // Timed detonation
  fuseTime?: number; // Seconds until detonation animation plays (default undefined = no fuse)
  detonationAnim?: string; // Animation to play on detonation (e.g., 'explode')
  detonationRadius?: number; // Damage radius for explosion (meters)
  detonationDamage?: number; // Damage at center of explosion
  
  // Visual effects
  light?: ProjectileLightSpec; // Light that follows the projectile
  impactParticlePreset?: string; // Particle preset to spawn on impact/destroy
  
  // Static FX options (type: 'static' only)
  attachToBone?: boolean; // If true, FX stays attached to muzzleBone (default true for static)
  fadeInDuration?: number; // Seconds to fade in (default 0)
  fadeOutDuration?: number; // Seconds to fade out before destroy (default 0.2)
}

/**
 * Light attached to a projectile.
 */
export interface ProjectileLightSpec {
  color?: Color3; // Light color (default white)
  intensity?: number; // Light intensity (default 1.0)
  range?: number; // Light range in units (default 5.0)
  offset?: Vector3; // Local offset from projectile center
}

export interface ProjectileHandoverContext {
  readonly attackerId: string;
  readonly attack: AttackDef;
  readonly spec: ProjectileSpec;
  readonly attachment: {
    readonly boneName?: string;
    readonly position: Vector3;
    readonly forward: Vector3;
    readonly up: Vector3;
    readonly right: Vector3;
  };
  readonly character: {
    readonly position: Vector3;
    readonly velocity: Vector3;
    readonly forward: Vector3;
  };
  readonly defaults: {
    readonly position: Vector3;
    readonly direction: Vector3;
    readonly speed: number;
    readonly velocity: Vector3;
  };
}

export interface ProjectileLaunchDirective {
  position?: Vector3;
  direction?: Vector3;
  speed?: number;
  velocity?: Vector3;
}

/**
 * Active weapon instance attached to a character.
 */
export interface WeaponInstance {
  id: string; // Unique instance ID
  ownerId: string; // Character that owns this weapon
  attackDefId: string; // Attack definition ID
  weaponMesh: AbstractMesh; // Visual weapon mesh
  collisionMesh?: AbstractMesh; // Low-poly collision submesh
  attachedBone?: string; // Bone name weapon is attached to
  hitboxEnabled: boolean; // Whether damage zone is active
  hasHit: boolean; // Whether weapon has hit during current enabled window
  normalizedTimeStart: number; // Normalized time when attack started
  animationGroup?: AnimationGroup; // Weapon's own animation (idle, attack, hit)
  hasDedicatedCollisionMesh: boolean; // True if has hm_collision mesh, false if using bounding box fallback
  isFromSubmesh?: boolean; // True if weapon was found from character submeshes (should never be hidden)
}

/**
 * Active projectile instance (kinematic or ballistic).
 */
export interface ProjectileInstance {
  id: string; // Unique instance ID
  ownerId: string; // Character that fired this projectile
  attackDefId: string; // Attack definition ID
  spec: ProjectileSpec; // Projectile specification
  mesh: AbstractMesh; // Visual projectile mesh
  collisionMesh?: AbstractMesh; // Low-poly collision submesh
  position: Vector3; // Current world position
  velocity: Vector3; // Current velocity (kinematic or ballistic)
  spawnTime: number; // Scene time when spawned
  spawnPosition: Vector3; // Spawn position for distance tracking
  lastPosition: Vector3; // Previous frame position (for segment casting)
  
  // Ballistic-specific
  physicsImpostor?: any; // Ammo physics impostor (if ballistic)
  hasCollided?: boolean; // Whether projectile has hit something (for sticky behavior)
  stuckToMesh?: AbstractMesh; // Mesh projectile is stuck to (if sticky)
  
  // Timed detonation
  fuseStartTime?: number; // Scene time when fuse started
  detonated?: boolean; // Whether detonation has occurred
  
  // Animation
  animationGroup?: AnimationGroup; // Projectile's own animation
  
  // Visual effects
  light?: PointLight; // Attached light following the projectile
}

/**
 * Active attack instance being tracked by CombatSystem.
 */
export interface ActiveAttack {
  attackerId: string;
  attackDef: AttackDef;
  startTime: number; // Scene time when attack started
  startDirection?: Vector3; // Facing direction when attack started (for projectile aiming)
  processedEvents: Set<number>; // Track which event indices have been processed
  weaponInstance?: WeaponInstance;
  projectileInstance?: ProjectileInstance;
  preparedProjectile?: {
    mesh: AbstractMesh;
    boneName?: string;
  };
  type?: AttackType;
  /**
   * Internal safety flag to avoid repeatedly forcing hitbox off if an event is missed.
   */
  forcedHitboxOff?: boolean;
  channelStartTime?: number;
  lastTickTime?: number;
  tickCount?: number;
  attachedMeshInstances?: AttachedMeshInstance[];
  previousPhase?: StatePhase | null;
  binding?: 'action' | 'action2' | 'action3';
  isHeld?: () => boolean;
  recoveryCompleteSent?: boolean;

  // Locomotion vs attack override protection (base-layer oneshots)
  lockedExitTimeGroup?: AnimationGroup;
  previousExitTimeConfig?: { enabled: boolean; time: number } | undefined;
  
  // Channel state for presets
  channelState?: Record<string, unknown>;
  /** Active particle emission IDs (for cleanup) */
  activeParticleEmissions?: string[];
  /** Active channel light IDs (for cleanup) */
  activeChannelLights?: string[];
  /** Active static FX mesh instances (for cleanup on channel end) */
  activeStaticFx?: AbstractMesh[];
  /** Per-target damage cooldown tracking */
  targetDamageTimes?: Map<string, number>;
}

export type InterruptReason = 'damage' | 'player' | 'custom';

export interface InterruptContext {
  readonly attackerId: string;
  readonly attack: AttackDef;
  readonly reason: InterruptReason;
  readonly phase: StatePhase | null;
  canInterrupt: boolean;
}

export interface AttackTickContext {
  readonly attackerId: string;
  readonly attack: AttackDef;
  readonly normalizedTime: number;
  readonly phase: StatePhase | null;
  readonly deltaTime: number;
}

export interface ChannelStartContext {
  readonly attackerId: string;
  readonly attack: AttackDef;
  /** Actions API for this channel */
  readonly actions: ChannelActions;
  /** Shared state for presets to communicate */
  state: Record<string, unknown>;
}

export interface ChannelTickContext {
  readonly attackerId: string;
  readonly attack: AttackDef;
  readonly channelDuration: number;
  readonly tickCount: number;
  readonly buttonHeld: boolean;
  /** Normalized channel progress (0-1) if maxDuration is set */
  readonly normalizedTime: number;
  /** Actions API for this channel */
  readonly actions: ChannelActions;
  /** Shared state for presets to communicate */
  state: Record<string, unknown>;
  facingOverride?: Vector3; // Hook may set to override facing
  attachedMeshOverrides?: Array<{
    mesh: AttachedMeshInstance;
    position?: Vector3;
    rotation?: Vector3;
    scale?: Vector3;
  }>;
}

export interface ChannelEndContext {
  readonly attackerId: string;
  readonly attack: AttackDef;
  readonly reason: 'completed' | InterruptReason;
  readonly duration: number;
  readonly tickCount: number;
  /** Actions API for cleanup */
  readonly actions: ChannelActions;
  /** Shared state from channel */
  state: Record<string, unknown>;
}

export interface AttackEndContext {
  readonly attackerId: string;
  readonly attack: AttackDef;
  readonly completed: boolean;
  readonly phase: StatePhase | null;
  readonly bufferedInput?: string | null;
}

export interface AttackCancelContext {
  readonly attackerId: string;
  readonly attack: AttackDef;
  readonly reason: InterruptReason;
  readonly phase: StatePhase | null;
}

export const INPUT_BUFFER_WINDOW = 0.15; // seconds

/**
 * Combat event types emitted by CombatSystem via EventBus.
 */
export interface CombatAttackStartEvent {
  attackerId: string;
  attackDefId: string;
  targetPosition?: Vector3;
}

export interface CombatDamageEvent {
  attackerId: string;
  targetId: string;
  damage: number;
  position: Vector3;
  source?: 'melee' | 'projectile' | 'aoe';
  /** Incoming direction of the hit in world-space (approx). */
  impactDirection?: Vector3;
  /** Surface normal at impact point, if available. */
  impactNormal?: Vector3;
  attackDefId?: string;
  weaponId?: string;
  projectileId?: string;
  /** FX config to spawn at impact position */
  impactFX?: import('../systems/effects/FXPresets').FXConfig;
}

export interface CombatDeathEvent {
  characterId: string;
  killerId?: string;
}

export interface CombatParticleTriggerEvent {
  position: Vector3;
  particleId: string; // Particle effect ID to spawn
  ownerId?: string; // Character or projectile that triggered it
}

/**
 * Hit detection result from raycast/overlap tests.
 */
export interface HitResult {
  hit: boolean;
  targetId?: string; // Character ID that was hit
  position?: Vector3; // Hit position in world space
  normal?: Vector3; // Hit surface normal
  distance?: number; // Distance from ray origin
}
